// src/components/Layout.tsx
import React from "react";
import { Link } from "react-router-dom";
import { useTheme, ThemeMode } from "../theme";

type LayoutProps = {
  children: React.ReactNode;
};

function getThemeRoot(theme: ThemeMode): { rootClass: string; isDark: boolean } {
  switch (theme) {
    case "purple":
      return {
        rootClass:
          "min-h-screen w-screen flex flex-col bg-gradient-to-br from-[#130824] via-[#1f0b35] to-[#2a0d45] text-white",
        isDark: true,
      };
    case "dark":
      return {
        rootClass: "min-h-screen w-screen flex flex-col bg-slate-950 text-white",
        isDark: true,
      };
    case "maroon":
      return {
        rootClass:
          "min-h-screen w-screen flex flex-col bg-gradient-to-br from-[#fff5f5] via-[#fde2e4] to-[#f8c1cf] text-[#2b0f14]",
        isDark: false,
      };
    default:
      return {
        rootClass: "min-h-screen w-screen flex flex-col bg-slate-950 text-white",
        isDark: true,
      };
  }
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { theme, editorMode, setTheme, setEditorMode } = useTheme();
  const { rootClass, isDark } = getThemeRoot(theme);

  const isMaroonTheme = theme === "maroon";

  const headerBorder = isDark
    ? "border-b border-white/15 bg-transparent"
    : "border-b border-rose-200/70 shadow-[0_8px_30px_rgba(190,24,93,0.12)] bg-rose-50/60 backdrop-blur";

  const labelClass = isDark
    ? "text-[11px] text-white/70"
    : "text-[11px] text-rose-900/80";

  const pillBase =
    "px-2.5 py-1 rounded border text-[11px] transition-colors duration-150";
  const pillActiveDark = "bg-white text-slate-900 border-white";
  const pillInactiveDark =
    "bg-white/10 text-white/70 border-white/20 hover:bg-white/20";
  const pillActiveLight = "bg-[#7f1d1d] text-white border-[#7f1d1d]";
  const pillInactiveLight =
    "bg-white text-rose-900 border-rose-200 hover:bg-rose-100";

  const pillClass = (active: boolean) => {
    if (active) {
      return isDark ? pillActiveDark : pillActiveLight;
    }
    return isDark ? pillInactiveDark : pillInactiveLight;
  };

  return (
    <div className={rootClass}>
      {/* global helpers for glass panels & gradient buttons */}
      <style>{`
        .glass-panel {
          background: ${
            isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.96)"
          };
          backdrop-filter: blur(20px);
          border: 1px solid ${
            isDark ? "rgba(255,255,255,0.08)" : "rgba(148,163,184,0.7)"
          };
          border-radius: 12px;
        }
        .btn-gradient {
          background: linear-gradient(135deg,#a855f7,#ec4899);
        }
        .btn-gradient:hover {
          background: linear-gradient(135deg,#9333ea,#db2777);
        }
        .light-accent-bar {
          background: linear-gradient(90deg, #ede9fe, #e9d5ff);
        }
      `}</style>

      {/* HEADER */}
      <header
        className={`flex-shrink-0 ${headerBorder}`}
        style={{ height: 44 }}
      >
        <div className="px-4 lg:px-6 h-full flex items-center justify-between">
          {/* Left: logo + title */}
          <div className="flex items-center gap-2">
            <img
              src="/pronoia-wellness-hero2.webp"
              alt="Pronoia Wellness"
              className="h-6 w-auto object-contain"
            />
            <div className="flex flex-col leading-tight">
              <span
                className={
                  "text-lg font-bold " +
                  (isDark ? "text-amber-300" : "text-[#f59e0b]")
                }
                style={{ textShadow: isDark ? '0 2px 8px rgba(251,191,36,0.4)' : 'none' }}
              >
                Where'd I Put That?
              </span>
              <span
                className={
                  "text-[10px] tracking-[0.16em] uppercase " +
                  (isDark ? "text-amber-100/70" : "text-slate-600")
                }
              >
                A Scholarly Organizer
              </span>
            </div>
          </div>

          {/* Center: empty space for cleaner look */}
          <div className="hidden md:flex items-center gap-5">
            {/* Removed theme and editor toggles */}
          </div>

          {/* Right: minimal nav buttons */}
          <div className="flex items-center gap-2 text-[11px]">
            <Link to="/organizer/landing">
              <button
                className={
                  "gap-2 h-8 px-3 text-xs rounded border " +
                  (isDark
                    ? "text-amber-200 hover:text-amber-100 hover:bg-amber-500/10 border-amber-400/30"
                    : "text-rose-900 hover:text-rose-950 hover:bg-white border-rose-200 bg-rose-100/70")
                }
              >
                📒 Organizer
              </button>
            </Link>

            <button
              className={
                "px-2.5 py-1 rounded-full bg-transparent " +
                (isDark
                  ? "text-white/80 hover:bg-white/10"
                  : "text-rose-900 hover:bg-white")
              }
            >
              ?
            </button>
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-hidden">{children}</main>

      {/* FOOTER */}
      <footer
        className={
          "flex-shrink-0 border-t text-center text-[11px] py-2 px-4 " +
          (isDark
            ? "border-white/10 text-white/50"
            : "border-rose-200 text-rose-900 bg-rose-50/90")
        }
      >
        © 2025 Monica L. James. All rights reserved. ThesiScribe is the
        intellectual property of Monica L. James and The Black Betterment
        Quest ("Invitation to the BBQ").
      </footer>
    </div>
  );
};

export default Layout;